import { gql, useQuery } from '@apollo/client';
import {
  endOfDay,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useAppData } from '~/shared/contexts/AppDataContext';
import { useAuth } from '~/shared/contexts/AuthContext';
import {
  endOfDayInTimezone,
  endOfWeekInTimezone,
  formatInTimezone,
  startOfDayInTimezone,
  startOfMonthInTimezone,
  startOfWeekInTimezone,
} from '~/shared/utils/dateTimeUtil';

interface GymTimezoneContextValue {
  gymTimezone: string | null;
  loading: boolean;
  error: Error | undefined;
  formatInGymTz: (date: Date | string | number, format: string) => string;
  getTimezoneOffset: () => string;
  isLocalTimezoneDifferent: () => boolean;
  startOfDayInGymTz: (date: Date) => Date;
  endOfDayInGymTz: (date: Date) => Date;
  startOfMonthInGymTz: (date: Date) => Date;
  startOfWeekInGymTz: (date: Date) => Date;
  endOfWeekInGymTz: (date: Date) => Date;
  parseDateForGymTz: (date: Date) => Date;
}

const GymTimezoneContext = createContext<GymTimezoneContextValue | undefined>(
  undefined
);

const GET_GYM_TIMEZONE = gql`
  query GetGymTimezone {
    currentGym {
      id
      timezone
    }
  }
`;

interface GymTimezoneProviderProps {
  children: ReactNode;
}

export const GymTimezoneProvider: FC<GymTimezoneProviderProps> = ({
  children,
}) => {
  const { isReady, accessToken, gymId } = useAuth();
  const appData = useAppData();
  const skipQuery = appData !== undefined;

  const { loading: queryLoading, data, error } = useQuery(GET_GYM_TIMEZONE, {
    fetchPolicy: 'cache-first',
    skip: skipQuery || !isReady || !accessToken || !gymId,
    onError: error => {
      console.error('Failed to fetch gym timezone:', error);
    },
  });

  const loading = skipQuery ? appData.loading : queryLoading;

  const gymTimezone = useMemo(() => {
    if (skipQuery) return appData.gymTimezone;
    return data?.currentGym?.timezone || null;
  }, [skipQuery, appData, data]);

  const formatInGymTz = useCallback(
    (date: Date | string | number, formatStr: string) => {
      try {
        if (date == null) return '';

        // If no gym timezone, use browser local time (regular format)
        if (!gymTimezone) {
          const dateObj =
            typeof date === 'string' || typeof date === 'number'
              ? new Date(date)
              : date;

          // Validate date
          if (isNaN(dateObj.getTime())) {
            console.error('Invalid date provided to formatInGymTz:', date);
            return '';
          }

          return format(dateObj, formatStr);
        }
        // Use gym timezone conversion
        return formatInTimezone(date, formatStr, gymTimezone);
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    },
    [gymTimezone]
  );

  const getTimezoneOffset = useCallback(() => {
    // Get GMT offset for the timezone
    // e.g., "Asia/Jakarta" -> "GMT+7"
    if (!gymTimezone) return '';

    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: gymTimezone,
        hour: 'numeric',
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(part => part.type === 'timeZoneName');
      return offsetPart?.value || '';
    } catch {
      // Fallback for older browsers
      return '';
    }
  }, [gymTimezone]);

  const isLocalTimezoneDifferent = useCallback(() => {
    // If no gym timezone configured, there's no difference
    if (!gymTimezone) return false;
    try {
      const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return localTz !== gymTimezone;
    } catch {
      return false;
    }
  }, [gymTimezone]);

  const startOfDayInGymTz = useCallback(
    (date: Date) => {
      // If no gym timezone, use browser local time
      if (!gymTimezone) return startOfDay(date);
      return startOfDayInTimezone(date, gymTimezone);
    },
    [gymTimezone]
  );

  const endOfDayInGymTz = useCallback(
    (date: Date) => {
      // If no gym timezone, use browser local time
      if (!gymTimezone) return endOfDay(date);
      return endOfDayInTimezone(date, gymTimezone);
    },
    [gymTimezone]
  );

  const startOfMonthInGymTz = useCallback(
    (date: Date) => {
      // If no gym timezone, use browser local time
      if (!gymTimezone) return startOfMonth(date);
      return startOfMonthInTimezone(date, gymTimezone);
    },
    [gymTimezone]
  );

  const startOfWeekInGymTz = useCallback(
    (date: Date) => {
      // If no gym timezone, use browser local time (Sunday as week start)
      if (!gymTimezone) return startOfWeek(date, { weekStartsOn: 0 });
      return startOfWeekInTimezone(date, gymTimezone);
    },
    [gymTimezone]
  );

  const endOfWeekInGymTz = useCallback(
    (date: Date) => {
      // If no gym timezone, use browser local time (Saturday as week end)
      if (!gymTimezone) return endOfWeek(date, { weekStartsOn: 0 });
      return endOfWeekInTimezone(date, gymTimezone);
    },
    [gymTimezone]
  );

  const parseDateForGymTz = useCallback(
    (date: Date) => {
      if (!gymTimezone) {
        // Extract just the date part (ignoring timezone)
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        // Create new date with same Y-M-D at midnight UTC (start of day)
        return new Date(Date.UTC(year, month, day, 0, 0, 0));
      }

      // Get the date components in the gym timezone
      const zonedDate = utcToZonedTime(date, gymTimezone);
      // Create date at midnight UTC with the gym timezone's date
      return new Date(
        Date.UTC(
          zonedDate.getFullYear(),
          zonedDate.getMonth(),
          zonedDate.getDate(),
          0,
          0,
          0
        )
      );
    },
    [gymTimezone]
  );

  const value = useMemo(
    () => ({
      gymTimezone,
      loading,
      error,
      formatInGymTz,
      getTimezoneOffset,
      isLocalTimezoneDifferent,
      startOfDayInGymTz,
      endOfDayInGymTz,
      startOfMonthInGymTz,
      startOfWeekInGymTz,
      endOfWeekInGymTz,
      parseDateForGymTz,
    }),
    [
      gymTimezone,
      loading,
      error,
      formatInGymTz,
      getTimezoneOffset,
      isLocalTimezoneDifferent,
      startOfDayInGymTz,
      endOfDayInGymTz,
      startOfMonthInGymTz,
      startOfWeekInGymTz,
      endOfWeekInGymTz,
      parseDateForGymTz,
    ]
  );

  return (
    <GymTimezoneContext.Provider value={value}>
      {children}
    </GymTimezoneContext.Provider>
  );
};

export const useGymTimezone = (): GymTimezoneContextValue => {
  const context = useContext(GymTimezoneContext);
  if (context === undefined) {
    throw new Error('useGymTimezone must be used within a GymTimezoneProvider');
  }
  return context;
};
